import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { registrarAbaAtualComoViva } from 'app/utils/abaEstaViva'
import orcamentoReducer from 'app/redux/orcamentoSlice'
import coresAmbientesReducer from 'app/redux/corPorAmbienteSlice'
import corLinearProgressReducer from 'app/redux/corLinearProgressSlice'
import parametrosReducer from 'app/redux/parametrosSlice'
import parametrosPorItemReducer from 'app/redux/parametrosPorItemSlice'
import permissoesReducer from 'app/redux/permissoesSlice'
import colunasGridOrcamentoReducer from 'app/redux/colunasGridOrcamentoSlice'
import atualizacaoOrcamentoLegadoReducer from '../atualizacaoOrcamentoLegadoSlice'
import atualizacaoItensOrcamentoLegadoReducer from '../atualizacaoItensOrcamentoLegadoSlice'
import totalizadorReducer from '../totalizadorSlice'
import gridItensReducer from '../gridItensSlice'
import respostasFinalizacaoOrcamentoReducer from '../respostasFinalizacaoOrcamentoSlice'
import aguardandoProcessamentoReducer from '../aguardandoProcessamentoSlice'
import menuPrincipalReducer from '../menuPrincipalSlice'
import telaOrcamentoAlteradaNaoSalvaReducer from '../telaOrcamentoAlteradaNaoSalvaSlice'
import produtosSelecionadosReducer, { marcarProdutosSelecionadosAtualizadoAction } from '../produtosSelecionadosSlice'
import orcamentoEmAndamentoReducer, { salvarOrcamentoEmAndamentoAction, OrcamentoEmAndamento } from '../orcamentoEmAndamentoSlice'
import storage from 'redux-persist/lib/storage/session';
import storageLocal from 'redux-persist/lib/storage';
import { persistReducer, persistStore } from 'redux-persist';
import { thunk } from "redux-thunk";
import { timingMiddleware } from '../middleware/timingMiddleware'
import { IOrcamento } from '../../types/IOrcamento'
import { IItemOrcamento } from '../../types/IItemOrcamento'
import { CamposDescontoHabilitados, camposDescontoHabilitados } from 'app/components/totalizador/camposDescontoEditaveis'
import { ParametroPorItem } from 'app/types/Parametro'


const persistConfig = {
    key: 'root',
    storage,
    // 491123 — produtosSelecionados e orcamentoEmAndamento têm seu próprio persistReducer
    // (localStorage, ver abaixo) em vez de herdar este (sessionStorage) — sessionStorage não
    // sobrevive ao fechamento real do navegador, e ambos precisam sobreviver a isso: a sacola
    // para o aviso de recuperação de produtos, o orçamento em andamento para o aviso de
    // recuperação pós-login (ver useRecuperarOrcamentoEmAndamento.ts).
    blacklist: ['produtosSelecionados', 'orcamentoEmAndamento']
};

// 491123 — sacola de produtos persistida em localStorage (não sessionStorage): precisa sobreviver
// ao fechamento do navegador para permitir o aviso "existem produtos na sacola, deseja recuperar?"
// ao reabrir um orçamento (ver useRecuperarCestaProdutosAnterior.ts).
//
// 491123 (refinamento) — a chave é sufixada com o aba_id (gerado no login, ver LoginContainer.tsx;
// único por aba graças à detecção de aba duplicada em _Base.cshtml) para que CADA ABA tenha sua
// própria cesta isolada no localStorage. Antes, todas as abas da mesma origem liam/escreviam a
// MESMA chave — abrir uma segunda aba já "herdava" os produtos da primeira, e a segunda aba
// perguntava se queria "recuperar" uma cesta que a primeira aba ainda estava usando normalmente.
// Sem aba_id disponível ainda (ex: bootstrap da tela de Login, antes de logar), cai num sufixo fixo
// só para não quebrar — nenhuma cesta real existe nesse ponto.
export function obterChaveCestaProdutosPorAba(): string {
    const abaId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('aba_id') : null
    return `produtosSelecionados_${abaId ?? 'semAba'}`
}

// 482572 — por padrão, o redux-persist grava a chave no localStorage assim que a árvore é
// rehidratada (ação REHYDRATE, disparada por persistStore() logo no carregamento da página), mesmo
// que o slice ainda esteja no estado INICIAL — sem nenhum dispatch "de verdade" ainda. Isso criava
// persist:orcamentoEmAndamento_<abaId>/persist:produtosSelecionados_<abaId> só de visitar o site
// (menu principal), antes de entrar num orçamento ou selecionar qualquer produto para a cesta.
//
// Este wrapper de storage intercepta o setItem: se a CHAVE AINDA NÃO EXISTE no localStorage (nunca
// teve conteúdo real) E o estado sendo gravado ainda é "vazio" segundo `ehEstadoVazio`, ignora a
// escrita — a chave só passa a existir na primeira vez que houver conteúdo de verdade. Uma vez
// criada, escritas seguintes (inclusive de volta a vazio) acontecem normalmente; a remoção
// definitiva da chave já é feita explicitamente em outros pontos (ex: onSair em
// MenuPrincipalContainer.tsx).
function criarStorageQueSoCriaChaveComConteudoReal(storageBase: typeof storageLocal, ehEstadoVazio: (estado: any) => boolean) {
    return {
        ...storageBase,
        setItem: async (chave: string, valorSerializado: string) => {
            const chaveJaExiste = (await storageBase.getItem(chave)) != null
            if (chaveJaExiste) return storageBase.setItem(chave, valorSerializado)

            // redux-persist serializa cada campo do slice individualmente (JSON.stringify por
            // campo, não do objeto inteiro) — mesmo formato já tratado em
            // localizarOrcamentoEmAndamentoOrfaoDeOutraAba.ts/useRecuperarCestaProdutosAnterior.ts.
            let estado: any = {}
            try {
                const nivel1 = JSON.parse(valorSerializado)
                Object.keys(nivel1).forEach(campo => {
                    if (campo === '_persist') return
                    try { estado[campo] = JSON.parse(nivel1[campo]) } catch { estado[campo] = nivel1[campo] }
                })
            } catch { /* valor ilegível: trata como vazio, não cria a chave */ }

            if (ehEstadoVazio(estado)) return Promise.resolve()

            return storageBase.setItem(chave, valorSerializado)
        }
    }
}

const produtosSelecionadosPersistConfig = {
    key: obterChaveCestaProdutosPorAba(),
    // 482572 — só cria a chave quando há produto de verdade selecionado (ver
    // criarStorageQueSoCriaChaveComConteudoReal acima) — não ao simplesmente entrar no site/menu.
    storage: criarStorageQueSoCriaChaveComConteudoReal(storageLocal, estado => !Array.isArray(estado.produtos) || estado.produtos.length === 0)
};

const produtosSelecionadosPersistedReducer = persistReducer(produtosSelecionadosPersistConfig, produtosSelecionadosReducer);

// 491123 — snapshot do orçamento em andamento (itens + cliente + forma de pagamento + tipo de
// documento + numero) persistido em localStorage, independente do slice `orcamento` (que continua
// em sessionStorage, sem mudança de comportamento). Sobrevive ao fechamento do navegador para
// permitir o aviso de recuperação pós-login (ver _Menu.cshtml/useRecuperarOrcamentoEmAndamento.ts).
//
// 482572 — mesmo refinamento já aplicado à cesta (obterChaveCestaProdutosPorAba, acima): a chave é
// sufixada com o aba_id para que CADA ABA tenha seu próprio snapshot isolado no localStorage. Antes,
// todas as abas liam/escreviam a MESMA chave fixa ('orcamentoEmAndamento') — duas abas editando
// orçamentos diferentes ao mesmo tempo faziam uma sobrescrever o snapshot da outra silenciosamente.
// Como o snapshot precisa sobreviver ao FECHAMENTO da aba (aba_id daquela aba não existe mais para
// ninguém reivindicar), a checagem pós-login (_Menu.cshtml/verificarOrcamentoEmAndamentoAposLogin)
// não pode mais confiar só no estado rehidratado da PRÓPRIA aba — precisa também varrer órfãos de
// outras abas mortas, mesmo mecanismo de BroadcastChannel usado em
// localizarOrcamentoEmAndamentoOrfaoDeOutraAba.ts (espelha localizarCestasOrfasDeOutrasAbas).
export function obterChaveOrcamentoEmAndamentoPorAba(): string {
    const abaId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('aba_id') : null
    return `orcamentoEmAndamento_${abaId ?? 'semAba'}`
}

// 482572 — assim que o store carrega (ou seja, em QUALQUER página do app, já que ele é o store
// único do bundle), esta aba passa a segurar um lock nomeado com o próprio aba_id. É isso que
// permite às OUTRAS abas descobrirem que ela está viva por `navigator.locks.query()` — sinal
// conclusivo, sem o falso-negativo do ping de 400ms quando a aba está em segundo plano ou ocupada
// (ver abaEstaViva.ts / travaEntreAbas.ts). O navegador libera o lock sozinho quando a aba morre.
registrarAbaAtualComoViva(typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('aba_id') : null)

const orcamentoEmAndamentoPersistConfig = {
    key: obterChaveOrcamentoEmAndamentoPorAba(),
    // 482572 — só cria a chave ao entrar de fato num orçamento (ver
    // criarStorageQueSoCriaChaveComConteudoReal acima e extrairSnapshotOrcamentoEmAndamento abaixo,
    // que só dispara com TipoOrcamento definido) — não ao simplesmente entrar no site/menu. Itens
    // vazio NÃO conta como vazio aqui: um orçamento recém-criado sem itens ainda já é "entrar no
    // orçamento" — o critério é TipoOrcamento, não Itens.
    storage: criarStorageQueSoCriaChaveComConteudoReal(storageLocal, estado => estado.TipoOrcamento == null)
};

const orcamentoEmAndamentoPersistedReducer = persistReducer(orcamentoEmAndamentoPersistConfig, orcamentoEmAndamentoReducer);

const rootReducer = combineReducers({
    menuPrincipal: menuPrincipalReducer,
    orcamento: orcamentoReducer,
    corPorAmbiente: coresAmbientesReducer,
    corLinearProgress: corLinearProgressReducer,
    parametros: parametrosReducer,
    parametrosPorItem: parametrosPorItemReducer,
    permissoes: permissoesReducer,
    colunasGridOrcamento: colunasGridOrcamentoReducer,
    atualizacaoOrcamentoLegado: atualizacaoOrcamentoLegadoReducer,
    atualizacaoItensOrcamentoLegado: atualizacaoItensOrcamentoLegadoReducer,
    totalizador: totalizadorReducer,
    gridItens: gridItensReducer,
    respostasFinalizacaoOrcamento: respostasFinalizacaoOrcamentoReducer,
    aguardandoProcessamento: aguardandoProcessamentoReducer,
    telaOrcamentoAlteradaNaoSalva: telaOrcamentoAlteradaNaoSalvaReducer,
    produtosSelecionados: produtosSelecionadosPersistedReducer,
    orcamentoEmAndamento: orcamentoEmAndamentoPersistedReducer
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const ativarTimingMiddleware = false

const store = configureStore({
    reducer:persistedReducer,
    middleware: () => [
        thunk,
        ativarTimingMiddleware ? timingMiddleware : null
    ].filter(v => v != null) as any,
})

export const persistor = persistStore(store);

// 491123 — captura contínua do orçamento em andamento (itens + cliente + forma de pagamento +
// tipo de documento + numero) para orcamentoEmAndamentoSlice, sempre que o slice `orcamento`
// mudar. Vive aqui (module scope, não dentro de um componente React) para não depender de qual
// container está montado — cliente/forma de pagamento podem ser editados em abas diferentes de
// onde os itens são editados, e o snapshot precisa acompanhar ambos.
const TIPOS_ORCAMENTO_PERSISTIVEIS: IOrcamento["TipoOrcamento"][] = [0, 1, 2] // Venda, Outras Saídas, Transferência — não persistir "Importação REST" (3)

// 482572 — usado para expirar o snapshot por aba (persist:orcamentoEmAndamento_<abaId>/
// persist:produtosSelecionados_<abaId>) com base no timestamp próprio (SalvoEm/AtualizadoEm,
// gravado dentro do próprio conteúdo persistido — ver store.subscribe abaixo e
// produtosSelecionadosSlice.ts) — um único localStorage por aba, sem chave adicional por
// tipo+filial: quem recupera compara TipoOrcamento/CodigoFilialOrcamento do CONTEÚDO contra o
// orçamento aberto agora (ver useRecuperarOrcamentoEmAndamento.ts).
export const QUATRO_DIAS_EM_MS = 4 * 24 * 60 * 60 * 1000

// 482572 — campos que só entram no snapshot quando o campo correspondente do Totalizador está
// HABILITADO para o usuário (ver obterCamposDescontoHabilitados abaixo). Diferente dos demais
// campos, para estes um `undefined` significa "não pode ser recuperado" e precisa APAGAR o valor
// que já estava no snapshot — o merge normal preserva o valor anterior quando o novo é undefined
// (ver store.subscribe abaixo), o que manteria vivo um desconto capturado enquanto o campo ainda
// estava habilitado.
const CAMPOS_DESCONTO_CONDICIONADOS_A_HABILITACAO: (keyof CamposDescontoHabilitados)[] = [
    'PercentualDesconto',
    'ValorDesconto',
    'PercentualDescontoSobreMercadoriasNacionais',
    'ValorDescontoSobreMercadoriasNacionais',
    'PercentualDescontoSobreMercadoriasImportadas',
    'ValorDescontoSobreMercadoriasImportadas',
    'AplicaDescontoCashback',
]

// 482572 — mesmas regras usadas pelo <Totalizador> para desabilitar os campos (módulo
// camposDescontoEditaveis.ts, compartilhado com TotalizadorContainer.tsx), só que alimentadas
// pelos parâmetros/permissões que já estão no redux — o snapshot é capturado em module scope
// (store.subscribe), sem depender de nenhum componente montado. `parametros`/`parametrosPorItem`
// são preenchidos pelo cache de obterParametros/obterParametrosPorOidItem.
function obterCamposDescontoHabilitados(orcamento: IOrcamento): CamposDescontoHabilitados {
    return camposDescontoHabilitados({
        orcamento,
        // NÃO usa totalizador.desabilitarEdicoes aqui (diferente de TotalizadorContainer.tsx): esse
        // flag é por TELA — o orcamentoController liga ele ao entrar em Finalizar/Markup e desliga ao
        // voltar para as demais. Como o snapshot é escrito continuamente, usá-lo faria o desconto ser
        // apagado só porque o navegador foi fechado com a tela de Finalizar/Markup aberta. O motivo
        // DURÁVEL de bloqueio (orçamento somente leitura) continua valendo — é o que vai aqui e o que
        // camposDescontoHabilitados também checa por conta própria (SomenteLeitura/CodigoDAV).
        edicoesDesabilitadas: orcamento.SomenteLeitura === true,
        valorParametro: nome => store.getState().parametros?.[nome],
        valorParametroPorFilial: nome => ((store.getState().parametrosPorItem ?? []) as ParametroPorItem[])
            .find(p => p.Nome === nome && p.oidItem === orcamento.OidFilialOrcamento)?.Valor,
    })
}

// 482572 — um produto tinta vira VÁRIAS linhas no orçamento: a base (a linha que o usuário vê,
// PossuiSubItens = true) e um item subordinado por corante — ItemPedidoBase.prg::DefinirTinta grava
// o código do produto tinta final (CODPROTINTA/CodigoProdutoTinta) em TODAS elas, mudando só o
// `Codigo`, que é o do componente (base 'B' / corante 'C'). Só a base entra no snapshot: na
// restauração o servidor regenera os corantes sozinho a partir de produto tinta + cor
// (Orcamento.cs::AdicionarItem → Rules.AdicionarItemTinta → oItens.Adicionar(codigo, corTinta)) —
// mandar os corantes de volta tentaria incluí-los como produtos avulsos.
// O `.trim()` não é cosmético: CODPROTINTA é CHAR(5) e pode chegar só com espaços para produto
// que não é tinta (o próprio backend trata isso à mão em OrcamentoImpressao.cs) — sem ele, um
// produto comum com padding seria tratado como componente de tinta e sumiria do snapshot.
function ehComponenteSubordinadoDeTinta(item: IItemOrcamento): boolean {
    return (item.CodigoProdutoTinta ?? "").trim() !== "" && item.PossuiSubItens !== true
}

function extrairSnapshotOrcamentoEmAndamento(orcamento: IOrcamento): OrcamentoEmAndamento | null {
    if (orcamento?.TipoOrcamento == null || TIPOS_ORCAMENTO_PERSISTIVEIS.indexOf(orcamento.TipoOrcamento) === -1) return null

    // Orcamento aberto SOMENTE LEITURA nao gera snapshot: nao ha edicao pendente para recuperar
    // (a tela nao deixa alterar nada) e o servidor recusaria qualquer gravacao vinda dai. Sem
    // este guard o pedido consultado era persistido igual a um em edicao e voltava depois como
    // oferta de recuperacao pos-login - useRecuperarOrcamentoEmAndamento.ts ja recusava OFERECER
    // um somente leitura (ver verificarRecuperarOrcamentoNaoGravado), mas a gravacao continuava
    // acontecendo, e a checagem de _Menu.cshtml (cenario JA GRAVADO) nao tem esse filtro.
    if (orcamento.SomenteLeitura === true) return null

    const temConteudo = (orcamento.ItensOrcamento?.length ?? 0) > 0 || orcamento.CodigoCliente != null || orcamento.Numero != null
    if (!temConteudo) return null

    // 482572 — o snapshot é a fonte dos dados enviados para RESTAURAR o orçamento (ver
    // useRecuperarOrcamentoEmAndamento.ts / _Menu.cshtml → OrcamentoBase.prg::
    // _RestaurarDadosPendentesClassesFilhasAposItens). Um desconto que a tela NÃO deixa o usuário
    // editar não é uma edição pendente dele: ou é resultado de regra/plano de pagamento (o servidor
    // recalcula sozinho), ou é o campo geral escondido pela separação Nacional/Importado — em
    // qualquer dos casos, mandar de volta faria a restauração forçar um desconto que o usuário não
    // digitou (inclusive dobrando o valor, quando o geral vai junto com os pares por origem).
    const descontoHabilitado = obterCamposDescontoHabilitados(orcamento)

    return {
        Numero: orcamento.Numero,
        TipoOrcamento: orcamento.TipoOrcamento,
        CodigoFilialOrcamento: orcamento.CodigoFilialOrcamento,
        CodigoCliente: orcamento.CodigoCliente,
        CodigoFormaPagamento: orcamento.CodigoFormaPagamento,
        CodigoTipoDocumento: orcamento.CodigoTipoDocumento,

        // Resumo do Orçamento — reflete o último valor CONFIRMADO em `orcamento` (após "Aplicar");
        // a edição ainda não aplicada é capturada à parte por ResumoOrcamento.tsx via
        // atualizarCamposPendentesOrcamentoEmAndamentoAction e preservada pelo merge abaixo.
        TipoOperacao: orcamento.TipoOperacao,
        PedidoCompraCliente: orcamento.PedidoCompraCliente,
        FinalidadeVenda: orcamento.FinalidadeVenda,
        DataValidade: orcamento.DataValidade,
        ObservacaoNF: orcamento.ObservacaoNF,
        ObservacaoOrcamento: orcamento.ObservacaoOrcamento,
        UsarObservacaoTextoLivreNF: orcamento.UsarObservacaoTextoLivreNF,

        // Endereços — Obra já é escrita em `orcamento.IdObra` assim que selecionada (sem etapa de
        // "Aplicar"); Entrega/Cobrança/Faturamento não têm representação em `orcamento` (ver
        // atualizarCamposPendentesOrcamentoEmAndamentoAction em GridEnderecosOrcamentoContainer.tsx).
        IdObra: orcamento.IdObra,

        // Entrega — turno/manter reserva/datas na capa já vivem em `orcamento` hoje; entrega
        // expressa/bloquear desdobramento não têm campo em IOrcamento (capturados à parte).
        TurnoEntrega: orcamento.TurnoEntrega,
        DescricaoTurnoEntrega: orcamento.DescricaoTurnoEntrega,
        ManterReserva: orcamento.ManterReserva,
        DataPrevisaoEntrega: orcamento.DataPrevisaoEntrega,
        DataMarcadaEntrega: orcamento.DataMarcadaEntrega,

        // Totalizador.tsx — todo campo editável já é gravado em `orcamento` a cada alteração
        // (sem etapa de "Aplicar" separada), então o snapshot já reflete o valor em tempo real.
        // Os campos de desconto passam pelo filtro de habilitação (ver descontoHabilitado acima).
        PercentualDesconto: descontoHabilitado.PercentualDesconto ? orcamento.PercentualDesconto : undefined,
        ValorDesconto: descontoHabilitado.ValorDesconto ? orcamento.ValorDesconto : undefined,
        PercentualDescontoSobreMercadoriasNacionais: descontoHabilitado.PercentualDescontoSobreMercadoriasNacionais ? orcamento.PercentualDescontoSobreMercadoriasNacionais : undefined,
        ValorDescontoSobreMercadoriasNacionais: descontoHabilitado.ValorDescontoSobreMercadoriasNacionais ? orcamento.ValorDescontoSobreMercadoriasNacionais : undefined,
        PercentualDescontoSobreMercadoriasImportadas: descontoHabilitado.PercentualDescontoSobreMercadoriasImportadas ? orcamento.PercentualDescontoSobreMercadoriasImportadas : undefined,
        ValorDescontoSobreMercadoriasImportadas: descontoHabilitado.ValorDescontoSobreMercadoriasImportadas ? orcamento.ValorDescontoSobreMercadoriasImportadas : undefined,
        ValorDespesasInclusas: orcamento.ValorDespesasInclusas,
        ValorAcrescimoFinanceiro: orcamento.ValorAcrescimoFinanceiro,
        PercentualSeguroEntrega: orcamento.PercentualSeguroEntrega,
        ValorSeguroEntrega: orcamento.ValorSeguroEntrega,
        PercentualFrete: orcamento.PercentualFrete,
        ValorFrete: orcamento.ValorFrete,
        ValorServicoAdicionalDespesa: orcamento.ValorServicoAdicionalDespesa,
        AplicaDescontoCashback: descontoHabilitado.AplicaDescontoCashback ? orcamento.AplicaDescontoCashback : undefined,
        CodigoMotivoDesconto: orcamento.CodigoMotivoDesconto,
        IdCanalDeVenda: orcamento.IdCanalDeVenda,

        Itens: (orcamento.ItensOrcamento ?? []).filter(item => !ehComponenteSubordinadoDeTinta(item)).map(item => ({
            Codigo: item.Codigo,
            // Tinta: identificam o produto tinta + a cor escolhida, usados para RECRIAR o item na
            // restauração (ver comentário em ehComponenteSubordinadoDeTinta acima e
            // converterItemParaProdutoMinimo em useRecuperarOrcamentoEmAndamento.ts).
            CodigoProdutoTinta: item.CodigoProdutoTinta,
            CodigoCorTinta: item.CodigoCorTinta,
            Quantidade: item.Quantidade,
            CodigoTipoEntrega: item.CodigoTipoEntrega,
            CodigoFilialRetirada: item.CodigoFilialRetirada,
            CodigoFilialTransferencia: item.CodigoFilialTransferencia,
            CodigoTipoFaturamento: item.CodigoTipoFaturamento,
            DataPrevisaoEntrega: item.DataPrevisaoEntrega,
            DataMarcadaEntrega: item.DataMarcadaEntrega,
            TurnoEntrega: item.TurnoEntrega,
            DescricaoTurnoEntrega: item.DescricaoTurnoEntrega,
            VendaSobEncomenda: item.VendaSobEncomenda,
            ObservacaoDoItem: item.ObservacaoDoItem,
            Index: item.Index,
            PrecoProduto: item.PrecoProduto,
            Ambiente: item.Ambiente,
            Lote: item.Lote,
            Carga: item.Carga,
            DadosMadeira: item.DadosMadeira != null ? { QuantidadeSolicitada: item.DadosMadeira.QuantidadeSolicitada } : undefined,
        }))
    }
}

let ultimoSnapshotOrcamentoEmAndamentoSerializado: string | null = null

store.subscribe(() => {
    const snapshotDoOrcamento = extrairSnapshotOrcamentoEmAndamento(store.getState().orcamento)
    const snapshotSerializado = snapshotDoOrcamento ? JSON.stringify(snapshotDoOrcamento) : null

    if (snapshotSerializado === ultimoSnapshotOrcamentoEmAndamentoSerializado) return
    ultimoSnapshotOrcamentoEmAndamentoSerializado = snapshotSerializado

    // Limpar (orcamentoSlice.actions.limparInformacoesOrcamento) já é tratado pelo próprio
    // extraReducers de orcamentoEmAndamentoSlice — aqui só grava quando há conteúdo real, nunca
    // dispara limpeza por conta própria (evita apagar o snapshot em transições transitórias do
    // slice `orcamento`, ex: navegação entre abas antes dos dados chegarem do servidor).
    if (snapshotDoOrcamento) {
        // Mescla por cima do snapshot atual (que pode conter campos "pendentes" só capturados via
        // atualizarCamposPendentesOrcamentoEmAndamentoAction — ver comentário no slice) preservando
        // qualquer campo que `orcamento` ainda não tem valor (undefined) — só sobrescreve com o que
        // veio de `orcamento` quando ele realmente tem uma informação mais nova/confirmada.
        const snapshotAtual = store.getState().orcamentoEmAndamento as OrcamentoEmAndamento
        const snapshotMesclado: OrcamentoEmAndamento = { ...snapshotAtual }
        Object.keys(snapshotDoOrcamento).forEach(chave => {
            const valor = (snapshotDoOrcamento as any)[chave]
            if (valor !== undefined) {
                (snapshotMesclado as any)[chave] = valor
                return
            }
            // 482572 — único caso em que undefined APAGA em vez de preservar: campo de desconto
            // desabilitado (ver CAMPOS_DESCONTO_CONDICIONADOS_A_HABILITACAO). O campo pode ter sido
            // capturado enquanto ainda estava habilitado (ex: antes de o plano de pagamento assumir
            // o desconto, ou antes de a separação Nacional/Importado esconder o campo geral) — sem
            // apagar, esse valor velho continuaria sendo enviado na restauração.
            if (CAMPOS_DESCONTO_CONDICIONADOS_A_HABILITACAO.indexOf(chave as keyof CamposDescontoHabilitados) !== -1) {
                delete (snapshotMesclado as any)[chave]
            }
        })
        // 482572 — timestamp gravado dentro do próprio snapshot (mesma chave por aba, sem chave
        // adicional) — usado para expirar após QUATRO_DIAS_EM_MS (ver localizarOrcamentoEmAndamentoOrfaoDeOutraAba.ts).
        snapshotMesclado.SalvoEm = Date.now()
        store.dispatch(salvarOrcamentoEmAndamentoAction(snapshotMesclado))
    }
})

// 482572 — mesmo princípio acima, aplicado à cesta de produtos: timestamp gravado dentro do
// próprio slice persistido (produtosSelecionadosSlice.ts/AtualizadoEm), sem chave adicional —
// usado para expirar após QUATRO_DIAS_EM_MS (ver useRecuperarCestaProdutosAnterior.ts). Guarda pelo
// conteúdo de `produtos` (não pelo timestamp) para não reagir ao próprio dispatch que ele dispara.
let ultimoProdutosSelecionadosSerializado: string | null = null

store.subscribe(() => {
    const produtos = (store.getState().produtosSelecionados as any)?.produtos ?? []
    const serializado = JSON.stringify(produtos)
    if (serializado === ultimoProdutosSelecionadosSerializado) return
    ultimoProdutosSelecionadosSerializado = serializado

    if (produtos.length === 0) return

    store.dispatch(marcarProdutosSelecionadosAtualizadoAction(Date.now()))
})

export default store

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

